import React from "react";
import { Col, Row } from "react-bootstrap";
import BootstrapTable from "react-bootstrap-table-next";
import correctIcon from "resources/images/dynamic_score_reports/skills/score-report/check-circle.png";
import incorrectIcon from "resources/images/dynamic_score_reports/skills/score-report/times.png";
import { tktCorrectData, ttTypingPassage } from "./candidateResponseConstants";

export const generateTables = (testId, testScores, isTT, isTestMakerTT) => {
  if (!testScores || testScores.size < 1) {
    return null;
  }

  if (isTT || isTestMakerTT) {
    return (
      <div // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: testScores.get("correctedTypedText"),
        }}
        className="ml-3"
      />
    );
  }

  const isTM = String(testId).substring(0, 2).toUpperCase() === "TM";

  // Fetch the table data, there is no data being returned, return null
  const {
    tableData,
    columns = [],
    tableCount = 0,
  } = getTableStructure(testId, testScores, isTM);

  if (tableData.length === 0) {
    return null;
  }

  // Some tests display their data in 2 tables.
  if (tableCount === 2) {
    const datasetSize = tableData.length / 2;
    const dataset1 = tableData.slice(0, datasetSize);
    const dataset2 = tableData.slice(datasetSize);

    return [
      <Col key="1">
        <BootstrapTable
          keyField="correctData"
          data={dataset1}
          columns={columns}
          bordered={false}
        />
      </Col>,
      <Col key="2">
        <BootstrapTable
          key="2"
          keyField="correctData"
          data={dataset2}
          columns={columns}
          bordered={false}
        />
      </Col>,
    ];
  }

  return (
    <Col>
      <BootstrapTable
        keyField="id"
        data={tableData}
        columns={columns}
        bordered={false}
      />
    </Col>
  );
};

// The return value should be {'tableData':[],columns:[],tableCount:number}
const getTableStructure = (testId, testScores, isTM) => {
  const tableStructure = {
    tableData: [],
    columns: [],
    tableCount: 0,
  };

  if (isTM) {
    // -------------------- Data Logic --------------------
    const questions = testScores && testScores.get("questions");
    if (!questions) {
      return tableStructure;
    }

    let totalTimeTaken = 0;
    questions.forEach((question, i) => {
      totalTimeTaken += question.get("timeTaken");
      tableStructure.tableData.push({
        correct: question.get("isCorrect"),
        questionData: (
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: question.get("questionStem"),
            }}
          />
        ),
        answerGiven:
          question.get("answerGiven") || question.get("longAnswerGiven"),
        id: i,
      });
    });

    // -------------------- Columns Styles --------------------
    tableStructure.columns = [
      {
        dataField: "questionData",
        text: `Question (Total time taken: ${totalTimeTaken} seconds)`,
        headerStyle: { fontSize: "14px" },
        headerClasses: "sticky-element",
        style: { fontSize: "14px" },
      },
      {
        dataField: "correct",
        text: "Correct?",
        headerStyle: { fontSize: "14px" },
        headerClasses: "sticky-element",
        classes: "text-center",
        formatter: correctColumnFormatter,
      },
      {
        dataField: "answerGiven",
        text: "Answer Given",
        headerStyle: { fontSize: "14px" },
        headerClasses: "sticky-element",
        style: { fontSize: "14px" },
      },
    ];
    tableStructure.tableCount = 1;

    return tableStructure;
  }

  switch (Number(testId)) {
    case 18: {
      // -------------------- Data Logic --------------------
      const candidateAnswers = testScores.get("typedText").split("\n");
      tktCorrectData.forEach((entry, i) => {
        const isCorrect = entry === candidateAnswers[i].trim() ? 1 : 0;

        tableStructure.tableData.push({
          correctData: entry,
          correct: isCorrect,
          dataEntered: candidateAnswers[i],
        });
      });

      // -------------------- Columns Styles --------------------
      tableStructure.columns = [
        {
          dataField: "correctData",
          text: "Correct Data",
          headerStyle: { width: "33.33%", fontSize: "14px" },
          headerClasses: "sticky-element",
          style: { fontSize: "14px" },
        },
        {
          dataField: "correct",
          text: "Correct?",
          headerStyle: { width: "33.33%", fontSize: "14px" },
          headerClasses: "sticky-element",
          formatter: correctColumnFormatter,
        },
        {
          dataField: "dataEntered",
          text: "Data Entered",
          headerStyle: { width: "33.33%", fontSize: "14px" },
          headerClasses: "sticky-element",
          style: { fontSize: "14px" },
          formatter: dataEnteredFormatter,
        },
      ];
      tableStructure.tableCount = 2;
      break;
    }
    case 21:
    case 22:
    case 32:
    case 33:
    case 35:
    case 36:
    case 38:
    case 39:
    case 40:
    case 41: {
      // -------------------- Data Logic --------------------
      const questions = testScores && testScores.get("questions");
      if (!questions) {
        break;
      }
      questions.forEach((question, i) => {
        tableStructure.tableData.push({
          correct: question.get("isCorrect"),
          questionData: question.get("questionStem"),
          timeTaken: `${question.get("timeTaken")} sec`,
          questionNumber: `${i + 1}.`,
          id: i,
        });
      });

      // -------------------- Columns Styles --------------------
      tableStructure.columns = [
        {
          dataField: "correct",
          text: "Correct?",
          headerStyle: { fontSize: "14px" },
          headerClasses: "sticky-element",
          classes: "text-center",
          formatter: correctColumnFormatter,
        },
        {
          dataField: "questionNumber",
          text: "",
          headerClasses: "sticky-element",
        },
        {
          dataField: "questionData",
          text: "Question",
          headerStyle: { fontSize: "14px" },
          headerClasses: "sticky-element",
          style: { fontSize: "14px" },
        },
        {
          dataField: "timeTaken",
          text: "Time Taken",
          headerStyle: { fontSize: "14px" },
          classes: "font-weight-bold",
          headerClasses: "sticky-element",
          style: { fontSize: "14px" },
        },
      ];
      tableStructure.tableCount = 1;

      break;
    }
    default:
  }

  return tableStructure;
};

export const correctColumnFormatter = (cell) => {
  if (cell === null) {
    return "";
  }

  return cell === 1 ? (
    <img src={correctIcon} alt="Correct" width="18" height="18" />
  ) : (
    <img src={incorrectIcon} alt="Incorrect" width="18" height="18" />
  );
};

const dataEnteredFormatter = (cell, row) => (
  <span className={row.correct === 0 ? "incorrect-data-entered" : ""}>
    {cell}
  </span>
);

export const getTypingPassage = () => (
  <Row className="gray-bottom-border mb-3 pb-2">
    <Col>
      <h2 className="score-detail-section-heading">Typing Passage</h2>
      <p className="font-italic">{ttTypingPassage}</p>
    </Col>
  </Row>
);
